/**
 * Media storage helpers on top of the file registry (Firebase Storage + Firestore).
 *
 * All media lives in Firebase Storage at users/{userId}/{workspace}/{category}/{filename}
 * with metadata in Firestore at users/{userId}/files/{fileId}; nodes hold `file://{fileId}`
 * references. This module adds the cache layer and the data-URL resolution that the
 * nodes and the offline queue need.
 *
 * Workspaces saved before the Firebase migration may still contain references from
 * the retired media backend; those are recognised only so the UI can show
 * "no longer available" instead of trying to fetch them.
 */

import { StorageCategory } from '../types';
import { logger } from './logger';
import { mediaCacheManager } from './cache';
import {
    uploadWithDeduplication,
    deleteRegisteredFile,
    getFileById,
    extractFileId,
    isFileReference,
} from './fileRegistry';

/** Prefix used by the retired media backend; such references can no longer be resolved. */
export const LEGACY_MEDIA_PREFIX = 'mongo://';
export const LEGACY_MEDIA_MESSAGE = 'This media is no longer available. Please generate or upload it again.';

// Deliberately not a type guard: callers pass `string`, and a guard would narrow the
// non-legacy branch to `never`.
export const isLegacyMediaRef = (ref: unknown): boolean =>
    typeof ref === 'string' && ref.startsWith(LEGACY_MEDIA_PREFIX);

export interface UploadMediaMetadata {
    category: StorageCategory;
    workspaceName: string;
    nodeId: string;
    nodeType?: string;
    prompt?: string;
    iterationCount?: number;
    negativePrompt?: string;
    model?: string;
    filename?: string;
}

/** Upload base64 media through the registry (deduplicated) and return its `file://` reference. */
export const uploadMedia = async (
    userId: string,
    base64Data: string,
    metadata: UploadMediaMetadata,
): Promise<{ id: string; fileRef: string }> => {
    const filename = metadata.filename || `${metadata.category.toLowerCase()}_${Date.now()}`;
    const dotIdx = filename.lastIndexOf('.');
    const basename = dotIdx > 0 ? filename.slice(0, dotIdx) : filename;
    const extension = dotIdx > 0 ? filename.slice(dotIdx + 1) : 'png';

    const fileRef = await uploadWithDeduplication(userId, base64Data, {
        category: metadata.category,
        workspaceName: metadata.workspaceName,
        displayNamePrefix: basename,
        extension,
        nodeId: metadata.nodeId,
        nodeType: metadata.nodeType,
        prompt: metadata.prompt,
        iterationCount: metadata.iterationCount,
        negativePrompt: metadata.negativePrompt,
        model: metadata.model,
    });

    return { id: extractFileId(fileRef) || fileRef, fileRef };
};

const fetchAsDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
};

/**
 * Resolve a media reference to something an <img>/<video> or an API call can use:
 * plain URLs and data URLs pass through, `file://` references are fetched from
 * Firebase Storage (cache first) and returned as data URLs, and legacy references throw.
 */
export const resolveMediaToDataUrl = async (mediaRef: string): Promise<string> => {
    if (isLegacyMediaRef(mediaRef)) throw new Error(LEGACY_MEDIA_MESSAGE);

    try {
        const cached = await mediaCacheManager.get(mediaRef);
        if (cached) return cached;
    } catch { /* ignore cache errors */ }

    if (!isFileReference(mediaRef)) return mediaRef;

    const { firebaseAuth } = await import('../firebaseConfig');
    const currentUid = firebaseAuth.currentUser?.uid;
    if (!currentUid) throw new Error('Not authenticated');

    const fileId = extractFileId(mediaRef);
    if (!fileId) throw new Error('Invalid media reference');

    const meta = await getFileById(currentUid, fileId, true);
    if (!meta?.downloadUrl) throw new Error('Media not found');

    const dataUrl = await fetchAsDataUrl(meta.downloadUrl);
    try {
        await mediaCacheManager.set(mediaRef, dataUrl, meta.contentType);
    } catch { /* ignore */ }
    return dataUrl;
};

/** Delete a `file://` reference from storage, the registry and the cache. */
export const deleteMediaRef = async (mediaRef: string): Promise<boolean> => {
    try {
        if (isLegacyMediaRef(mediaRef)) {
            try { await mediaCacheManager.delete(mediaRef); } catch { /* ignore */ }
            return true;
        }
        if (!isFileReference(mediaRef)) return false;

        const { firebaseAuth } = await import('../firebaseConfig');
        const currentUid = firebaseAuth.currentUser?.uid;
        if (!currentUid) return false;

        const fileId = extractFileId(mediaRef);
        if (!fileId) return false;

        const ok = await deleteRegisteredFile(currentUid, fileId);
        try { await mediaCacheManager.delete(mediaRef); } catch { /* ignore */ }
        return ok;
    } catch (error: any) {
        logger.error('mediaStorage', 'Delete failed', error);
        return false;
    }
};

// ── Cache convenience wrappers ────────────────────────────────────────────────

export const initializeMediaCache = async (): Promise<void> => {
    try {
        await mediaCacheManager.initialize();
    } catch (error) {
        console.warn('[mediaStorage] Cache initialization failed:', error);
    }
};

export const getMediaCacheStats = async () => mediaCacheManager.getStats();

export const clearMediaCache = async (): Promise<void> => {
    await mediaCacheManager.clearAll();
};

export const prefetchMedia = async (refs: string[]): Promise<void> => {
    await mediaCacheManager.prefetch(refs.filter(r => !isLegacyMediaRef(r)), resolveMediaToDataUrl);
};

export { mediaCacheManager };
